import { useState } from "react";
import { doc, deleteDoc, updateDoc, arrayUnion, collection, getDocs, query, where, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import Icon from "./Icon";
import EditSegmentModal from "./EditSegmentModal";
import AirportInfoModal from "./AirportInfoModal";
import NearbyModal from "./NearbyModal";
import { SEGMENT_TYPES, extractCountryFromAddress, extractCityFromAddress } from "../utils";

const IATA_TO_ICAO = {
  AA: 'AAL', UA: 'UAL', DL: 'DAL', BA: 'BAW', LH: 'DLH',
  AF: 'AFR', KL: 'KLM', IB: 'IBE', EK: 'UAE', QR: 'QTR',
  LX: 'SWR', TK: 'THY', AC: 'ACA', WN: 'SWA', B6: 'JBU',
  AS: 'ASA', F9: 'FFT', NK: 'NKS', HA: 'HAL', VS: 'VIR',
  EI: 'EIN', AY: 'FIN', SK: 'SAS', OS: 'AUA', FR: 'RYR',
  U2: 'EZY', CX: 'CPA', JL: 'JAL', NH: 'ANA', KE: 'KAL',
  SQ: 'SIA', MH: 'MAS', TG: 'THA', QF: 'QFA', NZ: 'ANZ',
  ET: 'ETH', AM: 'AMX', CM: 'CMP', AV: 'AVA', LA: 'LAN'
};

function getFlightAwareUrl(segment) {
  if (segment.type !== "flight") return null;
  const text = `${segment.detail || ""} ${segment.title || ""}`;
  const match = text.match(/\b([A-Z]{2})(\d{1,4})(?![A-Z0-9])/);
  if (!match) return null;
  const icao = IATA_TO_ICAO[match[1]] || match[1];
  return `https://www.flightaware.com/live/flight/${icao}${match[2]}`;
}

export default function SegmentCard({ segment, tripId, isSharedTrip, currentUserId, selected, onSelect, onAddSegment, allFaveRegions, savedFaves, weatherCache }) {
  const [open, setOpen] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showAirportInfo, setShowAirportInfo] = useState(false);
  const [showNearby, setShowNearby] = useState(false);
  const [showFaveModal, setShowFaveModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Fave modal state
  const [faveCity, setFaveCity] = useState("");
  const [faveCountry, setFaveCountry] = useState("");
  const [faveRegion, setFaveRegion] = useState("");
  const [faveNotes, setFaveNotes] = useState("");
  const [isWC, setIsWC] = useState(false);
  const [regionSuggestions, setRegionSuggestions] = useState([]);

  const cfg = SEGMENT_TYPES[segment.type] || SEGMENT_TYPES.activity;

  const siteUrl = segment.link
    ? segment.link.startsWith("http") ? segment.link : `https://${segment.link}`
    : null;

  const mapsUrl = segment.location
    ? `https://www.google.com/maps?q=${segment.location.lat},${segment.location.lng}`
    : (segment.address || segment.title)
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(segment.address || segment.title)}`
      : null;

  const flightAwareUrl = getFlightAwareUrl(segment);

  const openFaveModal = () => {
    setFaveCity(segment.faveCity || segment.wcCity || segment.destinationCity || extractCityFromAddress(segment.address) || "");
    setFaveCountry(segment.faveCountry || extractCountryFromAddress(segment.address) || "");
    setFaveRegion(segment.faveRegion || "");
    setFaveNotes(segment.faveNotes || segment.wcComment || "");
    setIsWC(segment.favorite || false);
    // Pre-load region suggestions if country already set
    const country = segment.faveCountry || extractCountryFromAddress(segment.address) || "";
    if (country && allFaveRegions) {
      setRegionSuggestions(allFaveRegions[country.toLowerCase()] || []);
    } else {
      setRegionSuggestions([]);
    }
    setShowFaveModal(true);
  };

  const handleFaveTap = () => {
    if (segment.fave) {
      // Toggle off — no modal needed
      updateDoc(doc(db, "trips", tripId, "segments", segment.id), {
        fave: false, favorite: false,
        faveCity: "", faveCountry: "", faveRegion: "", faveNotes: "",
      });
    } else {
      openFaveModal();
    }
  };

  const handleCountryChange = (val) => {
    setFaveCountry(val);
    setFaveRegion("");
    if (val.trim() && allFaveRegions) {
      setRegionSuggestions(allFaveRegions[val.trim().toLowerCase()] || []);
    } else {
      setRegionSuggestions([]);
    }
  };

  const handleSaveFave = async (skip = false) => {
    const city = skip ? (segment.faveCity || "") : faveCity.trim();
    const country = skip ? (segment.faveCountry || "") : faveCountry.trim();
    const region = skip ? (segment.faveRegion || "") : faveRegion.trim();
    const notes = skip ? (segment.faveNotes || "") : faveNotes.trim();

    await updateDoc(doc(db, "trips", tripId, "segments", segment.id), {
      fave: true,
      favorite: isWC,
      faveCity: city,
      faveCountry: country,
      faveRegion: region,
      faveNotes: notes,
      wcComment: "", wcDish: "",
    });

    // Upsert to savedRestaurants for persistent fave tracking
    try {
      const col = collection(db, "users", currentUserId, "savedRestaurants");
      const existing = await getDocs(query(col, where("title", "==", segment.title)));
      const savedData = {
        title: segment.title,
        faveCity: city,
        faveCountry: country,
        faveRegion: region,
        faveNotes: notes,
        address: segment.address || "",
        website: segment.link || "",
        fave: true,
        favorite: isWC,
        type: "restaurant",
        source: "trip",
      };
      if (existing.empty) {
        await addDoc(col, { ...savedData, createdAt: serverTimestamp() });
      } else {
        await updateDoc(existing.docs[0].ref, savedData);
      }
    } catch (e) {
      console.warn("savedRestaurants upsert failed:", e);
    }

    setShowFaveModal(false);
  };

  const handleDelete = () => {
    if (isSharedTrip) {
      setShowDeleteDialog(true);
    } else {
      if (window.confirm(`Remove "${segment.title}"?`)) {
        deleteDoc(doc(db, "trips", tripId, "segments", segment.id));
      }
    }
  };

  const handleDeleteForEveryone = async () => {
    setShowDeleteDialog(false);
    deleteDoc(doc(db, "trips", tripId, "segments", segment.id));
  };

  const handleHideForMe = async () => {
    setShowDeleteDialog(false);
    await updateDoc(doc(db, "trips", tripId, "segments", segment.id), {
      hiddenFor: arrayUnion(currentUserId),
    });
  };

  // Check savedFaves for persistent fave status across trips
  const savedEntry = savedFaves?.[segment.title?.toLowerCase()];
  const isFave = segment.fave || !!savedEntry;
  const isWorldClass = segment.favorite || (savedEntry?.favorite || false);

  return (
    <>
      <div
        className="segment-card"
        style={{
          opacity: selected ? 0.85 : 1,
          ...(segment.flagged ? { borderLeft: "3px solid #C05621" } : {}),
        }}
      >
        <div className="segment-main" onClick={() => setOpen(!open)}>
          <div
            onClick={(e) => { e.stopPropagation(); onSelect(segment.id); }}
            style={{
              width: 20, height: 20, borderRadius: 4, border: "1.5px solid var(--border)",
              background: selected ? "var(--ink)" : "white",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0, cursor: "pointer", marginRight: 2, transition: "background 0.15s",
            }}
          >
            {selected && (
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </div>

          <div className="segment-icon" style={{ background: cfg.bg, color: cfg.color }}>
            <Icon name={cfg.icon} size={15} />
          </div>
          <div className="segment-body">
            <div className="segment-title">
              {segment.title}
              {segment.flagged && (
                <span style={{
                  marginLeft: 6, fontSize: 10, fontWeight: 600, color: "#C05621",
                  background: "rgba(192,86,33,0.12)", borderRadius: 4, padding: "2px 6px",
                  textTransform: "uppercase", letterSpacing: "0.03em", verticalAlign: "middle",
                }}>
                  Updated
                </span>
              )}
              {isWorldClass && <span style={{ marginLeft: 6, fontSize: 12 }}>😋</span>}
              {isFave && !isWorldClass && (
                <span style={{ marginLeft: 5, display: "inline-flex", verticalAlign: "middle" }}>
                  <Icon name="faveBadge" size={14} />
                </span>
              )}
            </div>
            {segment.address && <div className="segment-detail">{segment.address}</div>}
            {!segment.address && segment.detail && <div className="segment-detail">{segment.detail}</div>}
          </div>
          {segment.time && !segment.checkoutDate && <div className="segment-time">{segment.time}</div>}
          {segment.checkoutDate && <div className="segment-time" style={{ fontSize: 11 }}>out {segment.checkoutDate}</div>}
          {(() => {
            if (segment.type === "flight" && flightAwareUrl) {
              return (
                <a
                  href={flightAwareUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={e => e.stopPropagation()}
                  style={{
                    fontSize: 11, color: "var(--ink-light)", flexShrink: 0,
                    display: "flex", alignItems: "center", gap: 2, marginLeft: 2,
                    textDecoration: "none", whiteSpace: "nowrap",
                  }}
                >
                  ✈️ <span style={{ color: "var(--accent, #3B6ECC)", fontWeight: 500 }}>Status</span>
                </a>
              );
            }
            if (!weatherCache || !segment.destinationCity) return null;
            const w = weatherCache[segment.destinationCity.toLowerCase()];
            if (!w) return null;
            return (
              <div style={{
                fontSize: 11, color: "var(--ink-light)", flexShrink: 0,
                display: "flex", alignItems: "center", gap: 2, marginLeft: 2,
              }}>
                {w.emoji} <span style={{ fontWeight: 500, color: "var(--ink-mid)" }}>{w.high}°</span>
                <span style={{ color: "var(--ink-faint)" }}>/{w.low}°</span>
              </div>
            );
          })()}
          <div className={`segment-chevron ${open ? "open" : ""}`}>
            <Icon name="chevronDown" size={14} />
          </div>
        </div>

        {open && (
          <div className="segment-expanded">
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
              {segment.address && (
                <div style={{ fontSize: 13, color: "var(--ink-mid)" }}>📍 {segment.address}</div>
              )}
              {segment.phone && (
                <div style={{ fontSize: 13, color: "var(--ink-mid)" }}>📞 {segment.phone}</div>
              )}
              {segment.checkoutDate && (
                <div style={{ fontSize: 13, color: "var(--ink-mid)" }}>
                  🗓 Check-in: {segment.date} · Check-out: {segment.checkoutDate}
                </div>
              )}
              {segment.flagged && (
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  background: "rgba(192,86,33,0.08)", border: "1px solid rgba(192,86,33,0.3)",
                  borderRadius: "var(--radius-sm)", padding: "8px 12px", fontSize: 13, color: "#C05621",
                }}>
                  <span>
                    ⚠ Possible change — check for a stale duplicate
                  </span>
                  <button
                    className="seg-btn"
                    style={{ fontSize: 11, padding: "3px 8px", flexShrink: 0, marginLeft: 8 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      updateDoc(doc(db, "trips", tripId, "segments", segment.id), { flagged: false });
                    }}
                  >
                    Dismiss
                  </button>
                </div>
              )}
              {segment.confirmation && (
                <div style={{ fontSize: 13, color: "var(--ink-mid)" }}>
                  🔖 Confirmation: <strong>{segment.confirmation}</strong>
                </div>
              )}
              {segment.detail && (
                <div style={{ fontSize: 13, color: "var(--ink-mid)", fontStyle: "italic" }}>
                  {segment.detail}
                </div>
              )}
              {/* Fave notes */}
              {isFave && segment.faveNotes && (
                <div style={{
                  background: "var(--paper-warm)", border: "1px solid var(--border)",
                  borderRadius: "var(--radius-sm)", padding: "8px 12px", marginTop: 4,
                }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: isWorldClass ? "var(--accent)" : "#B8860B", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    {isWorldClass ? "😋 World Class" : "Fave"}
                  </div>
                  <div style={{ fontSize: 13, color: "var(--ink-mid)", fontStyle: "italic" }}>
                    {segment.faveNotes}
                  </div>
                </div>
              )}
              {/* Legacy WC display for old data that hasn't been migrated */}
              {isWorldClass && !segment.faveNotes && (segment.wcDish || segment.wcComment) && (
                <div style={{
                  background: "var(--paper-warm)", border: "1px solid var(--border)",
                  borderRadius: "var(--radius-sm)", padding: "8px 12px", marginTop: 4,
                }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "var(--accent)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    😋 World Class
                  </div>
                  {segment.wcDish && (
                    <div style={{ fontSize: 13, color: "var(--ink)", marginBottom: 2 }}>
                      <strong>Dish:</strong> {segment.wcDish}
                    </div>
                  )}
                  {segment.wcComment && (
                    <div style={{ fontSize: 13, color: "var(--ink-mid)", fontStyle: "italic" }}>
                      {segment.wcComment}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="segment-actions">
              {siteUrl && (
                <a className="seg-btn primary" href={siteUrl} target="_blank" rel="noreferrer">
                  <Icon name="globe" size={12} /> Website
                </a>
              )}
              {flightAwareUrl && (
                <a className="seg-btn" href={flightAwareUrl} target="_blank" rel="noreferrer">
                  ✈️ Flight Status
                </a>
              )}
              {mapsUrl && (
                <a className="seg-btn" href={mapsUrl} target="_blank" rel="noreferrer">
                  <Icon name="map" size={12} /> Map
                </a>
              )}
              <button className="seg-btn" onClick={() => setShowEdit(true)}>
                <Icon name="edit" size={12} /> Edit
              </button>
              {segment.type === "flight" && (
                <button className="seg-btn" onClick={() => setShowAirportInfo(true)}>
                  🛡 Airport Info
                </button>
              )}
              {segment.type === "hotel" && (segment.address || segment.location) && (
                <button className="seg-btn" onClick={() => setShowNearby(true)}>
                  📍 Nearby
                </button>
              )}
              {segment.type === "restaurant" && (
                <>
                  <button
                    className="seg-btn"
                    onClick={handleFaveTap}
                    style={isFave ? { background: "var(--paper-warm)", borderColor: "var(--gold)" } : {}}
                    title={isFave ? "Remove from faves" : "Add to faves"}
                  >
                    <Icon name="faveBadge" size={13} />
                    {" "}{isFave ? (isWorldClass ? "World Class ✓" : "Fave ✓") : "Fave"}
                  </button>
                  {isFave && (
                    <button className="seg-btn" onClick={openFaveModal} title="Edit fave details">
                      <Icon name="edit" size={12} /> Notes
                    </button>
                  )}
                </>
              )}
              <button
                className="seg-btn"
                style={{ marginLeft: "auto", color: "#C05621", borderColor: "#C05621" }}
                onClick={handleDelete}
              >
                Remove
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Unified Fave modal */}
      {showFaveModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowFaveModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <div className="modal-title">
                <Icon name="faveBadge" size={16} />{" "}
                {isFave ? "Edit fave" : "Add to faves"}
              </div>
              <button className="modal-close" onClick={() => setShowFaveModal(false)}>
                <Icon name="close" size={14} />
              </button>
            </div>
            <div style={{ fontSize: 14, color: "var(--ink-mid)", marginBottom: 16 }}>
              {segment.title}
            </div>

            <div className="form-group">
              <label className="form-label">City</label>
              <input
                className="form-input"
                placeholder="e.g. Chicago, Seville, Tokyo..."
                value={faveCity}
                onChange={e => setFaveCity(e.target.value)}
                autoFocus
              />
            </div>

            <div className="form-group">
              <label className="form-label">Country</label>
              <input
                className="form-input"
                placeholder="e.g. USA, Spain, Japan..."
                value={faveCountry}
                onChange={e => handleCountryChange(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Region or area</label>
              <input
                className="form-input"
                placeholder={faveCountry.trim() ? "e.g. Tuscany, Madrid Metro, South of France" : "Enter country first"}
                value={faveRegion}
                onChange={e => setFaveRegion(e.target.value)}
                disabled={!faveCountry.trim()}
                style={!faveCountry.trim() ? { opacity: 0.5, cursor: "not-allowed" } : {}}
              />
              {regionSuggestions.length > 0 && !faveRegion && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                  {regionSuggestions.map(r => (
                    <button
                      key={r}
                      onClick={() => setFaveRegion(r)}
                      style={{
                        background: "var(--paper-warm)", border: "1px solid var(--border)",
                        borderRadius: 4, fontSize: 12, padding: "3px 8px",
                        cursor: "pointer", fontFamily: "var(--font-body)", color: "var(--ink-mid)"
                      }}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea
                className="form-input"
                placeholder="Impressions, tips, signature dish, what made it special..."
                value={faveNotes}
                onChange={e => setFaveNotes(e.target.value)}
                rows={3}
                style={{ resize: "none", fontFamily: "var(--font-body)", fontSize: 14 }}
              />
            </div>

            {/* World Class toggle */}
            <div
              onClick={() => setIsWC(!isWC)}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "12px 14px", marginBottom: 16,
                background: isWC ? "var(--paper-warm)" : "var(--paper-card)",
                border: `1px solid ${isWC ? "var(--accent)" : "var(--border)"}`,
                borderRadius: "var(--radius-sm)", cursor: "pointer",
              }}
            >
              <div>
                <div style={{ fontSize: 14, fontWeight: 500, color: "var(--ink)" }}>😋 World Class</div>
                <div style={{ fontSize: 12, color: "var(--ink-light)", marginTop: 2 }}>
                  Truly exceptional — hall of fame level
                </div>
              </div>
              <div style={{
                width: 44, height: 24, borderRadius: 12,
                background: isWC ? "var(--accent)" : "var(--border)",
                transition: "background 0.2s", position: "relative", flexShrink: 0,
              }}>
                <div style={{
                  width: 18, height: 18, borderRadius: "50%", background: "white",
                  position: "absolute", top: 3, left: isWC ? 23 : 3,
                  transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
                }} />
              </div>
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button className="submit-btn" onClick={() => handleSaveFave(false)} style={{ flex: 1 }}>
                Save
              </button>
              <button
                className="seg-btn"
                onClick={() => handleSaveFave(true)}
                style={{ flexShrink: 0 }}
              >
                Skip location
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete dialog */}
      {showDeleteDialog && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowDeleteDialog(false)}>
          <div className="modal">
            <div className="modal-header">
              <div className="modal-title">Remove segment</div>
              <button className="modal-close" onClick={() => setShowDeleteDialog(false)}>
                <Icon name="close" size={14} />
              </button>
            </div>
            <div style={{ fontSize: 14, color: "var(--ink-mid)", marginBottom: 20 }}>
              "{segment.title}"
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button
                className="seg-btn"
                style={{ padding: "12px 16px", textAlign: "left", fontSize: 14 }}
                onClick={handleHideForMe}
              >
                <div style={{ fontWeight: 500, color: "var(--ink)" }}>Hide from my view</div>
                <div style={{ fontSize: 12, color: "var(--ink-light)", marginTop: 2 }}>
                  Only you won't see it — other travelers are unaffected
                </div>
              </button>
              <button
                className="seg-btn"
                style={{ padding: "12px 16px", textAlign: "left", fontSize: 14, borderColor: "#C05621" }}
                onClick={handleDeleteForEveryone}
              >
                <div style={{ fontWeight: 500, color: "#C05621" }}>Remove for everyone</div>
                <div style={{ fontSize: 12, color: "var(--ink-light)", marginTop: 2 }}>
                  Permanently deletes this segment from the shared trip
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {showEdit && (
        <EditSegmentModal segment={segment} tripId={tripId} onClose={() => setShowEdit(false)} />
      )}
      {showAirportInfo && (
        <AirportInfoModal segment={segment} onClose={() => setShowAirportInfo(false)} />
      )}
      {showNearby && (
        <NearbyModal
          segment={segment}
          onClose={() => setShowNearby(false)}
          onAddSegment={onAddSegment}
        />
      )}
    </>
  );
}